import { useContext, useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faHeart,
  faCirclePlus,
  faTrash,
  faCopy,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as farHeart } from "@fortawesome/free-regular-svg-icons";
import useFormValidation from "../utils/useFormValidation";
import service from "../utils/request";
import Select from "react-select";
import message from "../utils/message";
import { SpaceContext } from "../SpaceContext";
import Checkbox from "./Checkbox";
import { getFileExtension, removeFileExtension } from "../utils/file";
import ShareLinkExpirationModal from "./ShareLinkExpirationModal";

export default function PropertiesModal({
  setIsPropertiesModalVisible,
  itemInfo,
  getFileList,
  spaceInfo,
  setCurrentItem,
  permissions,
}) {
  const [currentTab, setCurrentTab] = useState("Properties");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleClose = () => {
    setIsPropertiesModalVisible(false);
    setCurrentItem(null);
  };

  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
  }, [itemInfo]);

  const tabs = useMemo(() => {
    if (!itemInfo) return ["Properties"];

    const { is_folder } = itemInfo;

    // Use the permissions prop passed from App.js if itemInfo.permissions is missing
    const isWritable = itemInfo.permissions?.writable ?? permissions?.writable;

    if (isWritable) {
      return is_folder ? ["Properties"] : ["Properties", "Share Link"];
    }
    return ["Properties"];
  }, [itemInfo, permissions]);

  if (!itemInfo) {
    return null;
  }

  return (
    <div
      style={{ backgroundColor: "rgba(0, 0, 0, 0.2)" }}
      id="select-modal"
      tabIndex="-1"
      aria-hidden="true"
      className="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full"
    >
      <div className="relative w-full max-w-2xl max-h-full p-4">
        <div className="relative pb-4 bg-white rounded-lg shadow">
          <div className="flex items-center justify-between p-4 border-b rounded-t md:p-5">
            <h3 className="text-lg font-semibold text-gray-900 ">
              Item Properties
            </h3>
            <button
              onClick={handleClose}
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-sm text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900 ms-auto "
              data-modal-toggle="select-modal"
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <ul className="absolute z-10 flex flex-wrap pt-2 pl-4 text-sm font-medium text-center text-gray-500 bg-white border-gray-200 ">
            {tabs.map((item) => (
              <li key={item} onClick={() => setCurrentTab(item)}>
                <span
                  href="#"
                  aria-current="page"
                  className={`inline-block px-4 py-1 text-zinc-600
                                        rounded-t-lg active cursor-pointer  border-gray-300 border duration-200 transition-colors
                                        ${item === currentTab
                      ? "bg-gray-white border-b-0"
                      : "bg-gray-100"
                    } 
                                        ${item === "Properties" &&
                    tabs.length === 2 &&
                    "border-r-0"
                    }
                                        `}
                >
                  {item}
                </span>
              </li>
            ))}
          </ul>
          <div className="mx-4 mt-[36px] space-y-2 text-start flex-1 min-h-0 py-2 px-2 border-gray-300 border rounded-b-md rounded-tr-md overflow-y-auto">
            {currentTab === "Properties" && (
              <ProperitesContent
                itemInfo={itemInfo}
                getFileList={getFileList}
                spaceInfo={spaceInfo}
                setCurrentItem={setCurrentItem}
                setIsPropertiesModalVisible={setIsPropertiesModalVisible}
              />
            )}
            {currentTab === "User Access" && (
              <UserAccessContent itemInfo={itemInfo} />
            )}
            {currentTab === "Share Link" && (
              <ShareLinkContent key={refreshKey} itemInfo={itemInfo} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProperitesContent({
  itemInfo,
  getFileList,
  spaceInfo,
  setCurrentItem,
  setIsPropertiesModalVisible,
}) {

  const processTagClassesAndTags = (info) => {
    if (!info) return [];

    // item name
    const baseKeyword = removeFileExtension(info.item_name);

    const keywords = [
      {
        keyword_phrase: baseKeyword,
        label: baseKeyword,
        value: baseKeyword,
        isFirstTag: true,
      },
    ];

    if (info.file_tags) {
      info.file_tags.forEach(({ tag_id, tag, file_keywords_phrase }) => {
        keywords.push({
          keyword_phrase: file_keywords_phrase,
          label: tag,
          value: tag_id,
          isFirstTag: false,
        });
      });
    }
    return keywords;
  };

  const initialMark = (itemInfo?.public_mark ?? 0).toString();

  const [info, setInfo] = useState({
    ...itemInfo,
    item_mark: initialMark,
    item_name: itemInfo?.item_name,
    extension: itemInfo?.item_type,
    file_class: itemInfo?.file_class_id,
    is_favourite: itemInfo?.is_favour || false,
  });

  // const keywords = info.keywords?.split(", ") || [];
  const keywords = processTagClassesAndTags(info);

  const fileClass = info.file_class;

  function handleMarkClick(value) {
    if (parseInt(value) >= 7) {
      setInfo({ ...info, is_favourite: !info.is_favourite });
      return;
    }
    // Toggle off to unmark if clicking the currently selected color
    const nextMark = info.item_mark?.toString() === value.toString() ? "0" : value.toString();
    setInfo({ ...info, item_mark: nextMark });
  }

  async function handleDeleteClick() {
    const isInFavourFolder = spaceInfo?.space_id === "favour";
    const isInTrashFolder = spaceInfo?.space_id === "trash";

    let actionMessage, loadingMessage, successMessage;

    if (isInFavourFolder) {
      actionMessage = "remove from favourites";
      loadingMessage = "Removing from favourites...";
      successMessage = "Removed from favourites successfully";
    } else if (isInTrashFolder) {
      actionMessage = "permanently delete";
      loadingMessage = "Permanently deleting...";
      successMessage = "Delete successfully";
    } else {
      actionMessage = "delete";
      loadingMessage = "Moving to trash...";
      successMessage = "Files Moved To Trash Successfully";
    }

    const confirmed = window.confirm(`Are you sure you want to ${actionMessage} this item?`);
    if (!confirmed) return;

    try {
      message("loading", loadingMessage);

      if (isInFavourFolder) {
        await service.post("/filer/rename", {
          item_id: info.item_id,
          new_name: info.item_name + (!info.is_folder ? (info.extension || "") : ""),
          is_favour: false,
          file_class: info.file_class || 1,
          keywords: currentTags?.length > 0
            ? currentTags.map((tag) => tag.label).join(", ")
            : info.item_name,
        });
      } else if (isInTrashFolder) {
        await service.post("/filer/permanent_delete", [info.item_id]);
      } else {
        await service.post("/filer/delete", [info.item_id]);
      }

      message("success", successMessage);
      setIsPropertiesModalVisible(false);

      if (spaceInfo?.space_id !== "explorer") {
        await getFileList(spaceInfo.current_id);
      }
    } catch (error) {
      message("error", error);
    }
  }

  async function handleNameValueChange(e) {
    handleChange(e);
    const value = e.target.value;

    const updatedKeywords = [
      {
        keyword_phrase: value,
        label: value,
        value: value,
        isFirstTag: true,
      },
      ...keywords.filter((k) => !k.isFirstTag),
    ];

    setInfo({
      ...info,
      item_name: value,
      file_tags: updatedKeywords.slice(1).map((k) => ({
        tag: k.label,
        file_keywords_phrase: k.keyword_phrase,
      })),
    });
  }

  const [currentTags, setCurrentTags] = useState(keywords);

  function handleKeywordsValueChange(newKeywords) {
    setCurrentTags(newKeywords);
  }

  function handleFileClassValueChange(fileClass) {
    setInfo({ ...info, file_class: fileClass });
  }

  const validation = (value) => {
    if (!value) {
      return "Name is required.";
    }
    return "";
  };

  const { handleChange, handleSubmit, error } = useFormValidation(
    validation,
    itemInfo?.item_name
  );

  async function handleSaveClick(e) {
    e.preventDefault();
    const validationError = handleSubmit();
    if (validationError) return;

    try {
      const isNameChanged = info.item_name !== itemInfo.item_name;
      const extension = !info.is_folder && info.extension ? info.extension : "";
      const fullFileName = info.item_name + extension;

      // Check file existence if name changed
      if (isNameChanged) {
        message("loading", "Checking file name...");
        const targetFolderId = spaceInfo?.current_id || info.folder_id || spaceInfo?.space_id || "";
        const checkRes = await service.post("/filer/check_file_existence", {
          folder_id: targetFolderId,
          file_name: fullFileName,
          file_type: ".",
        });

        if (checkRes && checkRes.exists) {
          message("warn", "A file or folder with this name already exists.");
          return;
        }
      }

      message("loading", "Saving now...");

      // for new tag label input system
      const tagLabels =
        currentTags?.length > 0
          ? currentTags.map((tag) => tag.label).join(", ")
          : info.item_name;

      console.log("Tag labels being sent:", tagLabels);

      const payload = {
        item_id: info.item_id,
        new_name: info.item_name,
        file_class: info.file_class,
        keywords: tagLabels,
      };

      if (info.is_favourite !== itemInfo.is_favour) {
        payload.is_favour = info.is_favourite;
      }

      if (info.item_mark !== undefined && info.item_mark.toString() !== initialMark) {
        payload.new_mark = parseInt(info.item_mark);
      }

      await service.post("/filer/rename", payload);

      message("success", "Save successfully");
      setIsPropertiesModalVisible(false);
      if (spaceInfo?.space_id !== "explorer") {
        await getFileList(spaceInfo.current_id);
      }
    } catch (error) {
      message("error", error);
    }
  }
  console.log(spaceInfo);

  const colorMap = {
    0: "",
    1: "#ABEBC6",
    2: "#F9E79F",
    3: "#F8C471",
    4: "#EC7063",
    5: "#CB4335",
    6: "#7D3C98",
  };
  return (
    <div>
      <div className="relative ">
        <label className="block mb-2 text-sm font-medium text-left text-gray-900">
          Name
        </label>
        <input
          className="bg-gray-50 border outline-none border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          placeholder="file or folder name"
          required
          value={info?.item_name}
          onChange={handleNameValueChange}
        />
        {error && (
          <div className="err-msg" style={{ bottom: -22 }}>
            {error}
          </div>
        )}
      </div>
      <div className="mt-5">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-left text-gray-900">
            Favor
          </label>
          <div className="flex items-center gap-2">
            <div
              onClick={(e) => handleMarkClick("7")}
              className={`flex cursor-pointer justify-center items-center transition transform hover:scale-110 w-5 h-5 rounded-full duration-200`}
              title={info?.is_favourite ? "Remove from favorites" : "Add to favorites"}
            >
              <FontAwesomeIcon
                color={"#dc2626"}
                className="w-5 h-5"
                icon={info?.is_favourite ? faHeart : farHeart}
              />
            </div>
            {spaceInfo?.space_id === "favour" && (
              <button
                onClick={handleDeleteClick}
                type="button"
                className="flex items-center gap-2 text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-md text-sm px-3 py-1 transition-colors duration-200"
                title="Remove from favourites"
              >
                <FontAwesomeIcon icon={farHeart} className="w-3 h-3" />
                Remove from Favourites
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-4">
          {Object.keys(colorMap).map((item) => {
            return (
              <div
                onClick={() => handleMarkClick(item)}
                key={item}
                className={`flex cursor-pointer  justify-center items-center transition transform hover:scale-110 w-5 h-5 rounded-full duration-200`}
                style={{
                  backgroundColor: colorMap[item],
                  border: item === "0" ? "2px solid #8e8d92" : "none",
                }}
              >
                {item.toString() === info?.item_mark?.toString() && (
                  <FontAwesomeIcon
                    color={info?.item_mark?.toString() === "0" ? "#8e8d92" : "#fff"}
                    className="w-3 h-3"
                    icon={faCheck}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* <div className="flex items-center justify-end pr-2 space-x-2 rounded-b"> */}
      <div className="flex items-center justify-end space-x-2 rounded-b">
        <button
          onClick={handleSaveClick}
          data-modal-hide="default-modal"
          type="button"
          className={`text-white bg-[#ffa270] hover:bg-[#ffa270]/80 font-medium rounded-lg text-sm px-4 py-[8px] text-center}`}
        >
          Save
        </button>
      </div>
    </div>
  );
}

function UserAccessContent({ itemInfo }) {
  const { users } = useContext(SpaceContext);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [accessUsers, setAccessUsers] = useState([]);
  const [isChecked, setIsChecked] = useState(false);

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const handleSelectOpen = () => {
    setIsSelectOpen(true);
  };
  const handleSelectClose = () => {
    setIsSelectOpen(false);
  };

  const handleCheckboxChange = (isAll, index) => {
    const accessUsersSlice = [...accessUsers];
    if (isAll) {
      const result = accessUsers.map((user) => ({
        ...user,
        isSelected: !user.isSelected,
      }));
      setIsChecked(!isChecked);
      setAccessUsers(result);
    } else {
      accessUsersSlice[index] = {
        ...accessUsersSlice[index],
        isSelected: !accessUsersSlice[index].isSelected,
      };
      setAccessUsers(accessUsersSlice);
    }
  };

  const options = useMemo(() => {
    return users.map((item) => ({
      value: item.user_id,
      label: item.email,
    }));
  }, [users]);

  function handleUserSelect(value) {
    setSelectedUsers(value);
  }

  async function getAccessUsers() {
    service
      .get("/filer/get_access_users/" + itemInfo?.item_id)
      .then(setAccessUsers);
  }

  async function handleAddBtnClick() {
    if (selectedUsers?.length === 0) {
      message("error", "There is no users be selected");
      return;
    }
    try {
      message("loading", "Giving users access now...");
      await service.post("/filer/give_access", {
        item_id: itemInfo?.item_id,
        user_ids: selectedUsers.map((user) => user.value) || [],
      });
      setSelectedUsers([]);
      setIsChecked(false);
      await getAccessUsers();
      message("success", "Add users successfully");
    } catch (error) {
      message("error", error);
    }
  }

  async function removeAccess() {
    const selectedAccessUsers = accessUsers.filter((item) => item.isSelected);

    if (selectedAccessUsers?.length === 0) {
      message("error", "There is no users be selected");
      return;
    }
    try {
      message("loading", "Removing users access now...");
      await service.post("/filer/remove_access", {
        item_id: itemInfo?.item_id,
        user_ids: selectedAccessUsers.map((user) => user.user_id) || [],
      });
      setIsChecked(false);
      await getAccessUsers();
      message("success", "Remove users successfully");
    } catch (error) {
      message("error", error);
    }
  }

  useEffect(() => {
    getAccessUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`h-full ${isSelectOpen ? "" : "overflow-auto"}`}>
      <div>
        <label className="block mb-2 text-sm font-medium text-left text-gray-900">
          Name
        </label>
        <div className="flex gap-6">
          <Select
            // defaultValue={[colourOptions[2], colourOptions[3]]}
            isMulti={true}
            options={options}
            className="w-full basic-multi-select"
            classNamePrefix="select"
            onChange={handleUserSelect}
            value={selectedUsers}
            onMenuOpen={handleSelectOpen}
            onMenuClose={handleSelectClose}
            isOpen={isSelectOpen}
          />
          <button
            onClick={handleAddBtnClick}
            data-modal-hide="default-modal"
            type="button"
            className={`text-white h-[38px] flex items-center gap-2 bg-[#ffa270] hover:bg-[#ffa270]/80 font-medium rounded-md text-sm px-4 py-[8px] text-center}`}
          >
            <FontAwesomeIcon icon={faCirclePlus} />
            Add
          </button>
        </div>
      </div>
      <div className="w-full mt-4 border-b-2 border-gray-300 "></div>
      <div className="mt-4">
        <div className="flex justify-between mb-2 text-sm font-medium text-left text-gray-900">
          User Access
          <button
            onClick={removeAccess}
            data-modal-hide="default-modal"
            type="button"
            className={`text-white flex items-center gap-2 bg-red-500 hover:bg-red-500/80 font-medium rounded-md text-sm px-4 py-[8px] text-center}`}
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete
          </button>
        </div>
        <div className="rounded-md h-44">
          <div className="flex h-7 bg-zinc-200">
            <div className="flex items-center w-10 pl-2">
              <Checkbox
                onChange={() => handleCheckboxChange(true)}
                size=" h-5 w-5"
                color="bg-zinc-500"
                checked={isChecked}
              />
            </div>
            <div className="flex items-center flex-1 pl-4 text-zinc-800">
              Name
            </div>
          </div>
          <div className=" h-[148px] overflow-auto">
            {accessUsers.map((item, index) => (
              <div
                key={item?.id}
                className={`h-8  flex items-center ${(index + 1) % 2 === 0 ? "" : "bg-zinc-100"
                  } `}
              >
                <div className="flex items-center w-10 pl-2">
                  <Checkbox
                    checked={item.isSelected}
                    onChange={() => handleCheckboxChange(false, index)}
                    size=" h-5 w-5"
                    color="bg-zinc-500"
                  />
                </div>
                <div className="flex-1 pl-4 text-zinc-800">{item?.email}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareLinkContent({ itemInfo }) {
  const [links, setLinks] = useState([]);
  const [isExpirationModalOpen, setIsExpirationModalOpen] = useState(false);

  async function getShareLinks() {
    service.get("/filer/get_shared_links/" + itemInfo.item_id).then(setLinks);
  }

  useEffect(() => {
    getShareLinks();
  }, [itemInfo.item_id]);

  async function generateShareLink(daysValid) {
    try {
      message("loading", "Creating share link now...");
      await service.post("/filer/generate_sharelink", {
        item_id: itemInfo.item_id,
        days_valid: daysValid,
      });
      message("success", "Create share link successfully");
      setIsExpirationModalOpen(false);
      await getShareLinks();
    } catch (error) {
      message("error", error);
    }
  }

  function fallbackCopyTextToClipboard(text) {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const msg = successful ? "successful" : "unsuccessful";
      console.log("Fallback: Copying text command was " + msg);
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
    }

    document.body.removeChild(textArea);
  }

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        fallbackCopyTextToClipboard(text);
      }
      message("success", "Copy Successfully!");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDelete = async (linkId) => {
    message("loading", "Deleting now");
    await service.post("/filer/delete_shared_link", {
      item_id: itemInfo.item_id,
      link_ids: [linkId],
    });
    await getShareLinks();
    message("success", "Delete Link Successfully");
  };

  const isLinkExpired = (link) => {
    if (!link.expiration_date) return false;
    return new Date(link.expiration_date) < new Date();
  };

  const getExpirationTooltip = (link) => {
    if (!link.expiration_date) {
      return "Never expires";
    }
    return `Expires: ${new Date(link.expiration_date).toLocaleString()}`;
  };

  // Filter out expired links
  const activeLinks = links.filter(link => !isLinkExpired(link));
  const shareOrigin = window.location.origin.replace(/^http:/, "https:");

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setIsExpirationModalOpen(true)}
          data-modal-hide="default-modal"
          type="button"
          className={`text-white flex items-center gap-2 bg-[#ffa270] hover:bg-[#ffa270]/80 font-medium rounded-md text-sm px-4 py-[8px] text-center}`}
        >
          <FontAwesomeIcon icon={faCirclePlus} />
          Create New Link
        </button>
      </div>
      <div className="px-4 py-2 mt-4 overflow-auto border-2 border-gray-300 rounded h-60">
        {activeLinks.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            No active links. Create a new link to share.
          </div>
        ) : (
          activeLinks.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between w-full mb-1 cursor-pointer text-zinc-700 "
            >
              <div
                onClick={() => handleCopy(shareOrigin + item.link)}
                title={getExpirationTooltip(item)}
                className="w-[85%] border-b border-gray-800 overflow-hidden whitespace-nowrap text-ellipsis hover:text-blue-600"
              >
                {shareOrigin + item.link}
              </div>
              <div className="flex gap-2 ">
                <FontAwesomeIcon
                  icon={faCopy}
                  onClick={() => handleCopy(shareOrigin + item.link)}
                  title="Copy"
                  className="w-5 h-5 cursor-pointer text-zinc-600"
                />
                <FontAwesomeIcon
                  icon={faTrash}
                  onClick={() => handleDelete(item.id)}
                  title="Delete"
                  className="w-5 h-5 cursor-pointer text-zinc-600"
                />
              </div>
            </div>
          ))
        )}
      </div>

      <ShareLinkExpirationModal
        isOpen={isExpirationModalOpen}
        onClose={() => setIsExpirationModalOpen(false)}
        onSubmit={generateShareLink}
      />
    </div>
  );
}
